import { AfterViewInit, Component, computed, effect, input, output, signal, ViewChild, ViewContainerRef } from '@angular/core';

import { FormGroup } from '@angular/forms';
import { FormWizardStepDirective } from './form-wizard-step.directive';
import { ButtonComponent } from '../button/button.component';
import { TranslateModule } from '@ngx-translate/core';
import { ProgressStepsComponent, WorkUiProgressStepInterface } from '../progress-steps/progress-steps.component';
import { VisualScrollIndicatorComponent } from '../visual-scroll-indicator/visual-scroll-indicator.component';
import { LoadingAndFeedbackState } from '../../models/loading-and-feedback-state.type';

@Component({
  selector: 'work-ui-form-wizard',
  imports: [ButtonComponent, TranslateModule, ProgressStepsComponent, VisualScrollIndicatorComponent],
  templateUrl: './form-wizard.component.html',
  styleUrl: './form-wizard.component.scss',
})
export class FormWizardComponent implements AfterViewInit {
  @ViewChild('stepHost', { read: ViewContainerRef }) stepHost!: ViewContainerRef;
  public formGroupInput = input.required<FormGroup>();
  public completeButtonLabel = input<string>('SPEICHERN');
  public completeButtonDisabled = input<boolean>(false);
  public completeButtonLoadingState = input<LoadingAndFeedbackState>('none');
  public complete = output();
  protected currentIndexSignal = signal(0);
  protected steps = signal<FormWizardStepDirective[]>([]);
  protected currentIndex = this.currentIndexSignal.asReadonly();
  protected isCurrentStepValid = signal(false);
  protected currentStep = computed<FormWizardStepDirective | null>(() => this.steps()[this.currentIndex()] ?? null);
  protected progressStepList = computed<WorkUiProgressStepInterface[]>(() =>
    this.steps()
      .filter((step) => !step.stepHidden())
      .map((step) => ({
        label: step.stepTitle() || '',
        completed: false,
      })),
  );
  protected progressStepListActiveIndex = computed(() =>
    this.progressStepList().findIndex((step) => step.label === this.currentStep()?.stepTitle()),
  );
  private formGroupInputChangeEffect = effect(() => {
    this.formGroupInput().statusChanges.subscribe(() => this.isCurrentStepValid.set(this.getCurrentStepValid()));
  });

  ngAfterViewInit() {
    this.findFirstVisibleStep();
    this.renderCurrentStep();
  }

  public registerStep(step: FormWizardStepDirective) {
    this.steps.update((steps) => [...steps, step]);
  }

  protected next() {
    this.setStepCompleteness(true);
    let nextIndex = this.currentIndex() + 1;

    // Find the next visible step
    while (nextIndex < this.steps().length && this.steps()[nextIndex]?.stepHidden()) {
      nextIndex++;
    }

    if (nextIndex < this.steps().length && this.isCurrentStepValid()) {
      this.currentIndexSignal.set(nextIndex);
      this.renderCurrentStep();
    }
  }

  protected prev() {
    this.setStepCompleteness(false);
    let prevIndex = this.currentIndex() - 1;

    // Find the previous visible step
    while (prevIndex >= 0 && this.steps()[prevIndex]?.stepHidden()) {
      prevIndex--;
    }

    if (prevIndex >= 0) {
      this.currentIndexSignal.set(prevIndex);
      this.renderCurrentStep();
    }
  }

  private getCurrentStepValid(): boolean {
    const step = this.currentStep();
    if (!step) return false;
    const controlNames = step.formControlNames;
    if (controlNames.length === 0) return true;
    return controlNames.every((controlName) => this.formGroupInput().get(controlName)?.valid ?? false);
  }

  private findFirstVisibleStep() {
    let index = 0;
    while (index < this.steps().length && this.steps()[index]?.stepHidden()) {
      index++;
    }
    if (index < this.steps().length) {
      this.currentIndexSignal.set(index);
    }
  }

  private renderCurrentStep() {
    if (!this.stepHost) return;
    this.stepHost.clear();

    const step = this.currentStep();
    if (!step) return;
    this.stepHost.createEmbeddedView(step.template);
    this.isCurrentStepValid.set(this.getCurrentStepValid());
    step.stepActive.emit(true);
  }

  private setStepCompleteness(complete: boolean) {
    const matchingStep = this.progressStepList()[complete ? this.progressStepListActiveIndex() : this.progressStepListActiveIndex() - 1];
    if (matchingStep) {
      matchingStep.completed = complete;
    }
  }
}
